import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

export default function EMIPaidReport() {
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const [searchedStartDate, setSearchedStartDate] =
    useState(today);

  const [searchedEndDate, setSearchedEndDate] =
    useState(today);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formatDate = (value) => {
    if (!value) return "—";

    const parts = String(value).slice(0, 10).split("-");

    if (parts.length !== 3) {
      return value;
    }

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const load = async (
    selectedStart = startDate,
    selectedEnd = endDate
  ) => {
    if (!selectedStart || !selectedEnd) {
      setError("Please select both start date and end date.");
      return;
    }

    if (selectedEnd < selectedStart) {
      setError("End date cannot be before start date.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.get("/reports/emi-paid", {
        params: {
          start_date: selectedStart,
          end_date: selectedEnd
        }
      });

      const reportRows =
        res.data.records ||
        res.data.rows ||
        [];

      setRows(reportRows);
      setTotal(Number(res.data.total || 0));

      setSearchedStartDate(selectedStart);
      setSearchedEndDate(selectedEnd);

    } catch (e) {
      console.error(e);

      setRows([]);
      setTotal(0);

      setError(
        e?.response?.data?.detail ||
        "Unable to load EMI paid records."
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(today, today);
  }, []);

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const printSecurePdf = async () => {
    if (printing) return;

    if (!rows.length) {
      alert("No EMI paid records are available to print.");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      alert(
        "Your FinLend login session is not available. Please log in again."
      );
      return;
    }

    const popup = window.open("", "_blank");

    if (!popup) {
      alert(
        "Please allow pop-ups for FinLend to open the PDF."
      );
      return;
    }

    popup.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Generating EMI Paid Report</title>
        </head>
        <body style="
          font-family:Arial,sans-serif;
          padding:30px;
          color:#0b2545;
        ">
          <h3>Generating FinLend secure PDF...</h3>
          <p>Please keep this window open.</p>
        </body>
      </html>
    `);

    popup.document.close();

    setPrinting(true);

    try {
      // Load FinLend business profile for the secure PDF header.
      let business = {};

      try {
        const businessResponse = await api.get("/business-profile");
        business = businessResponse?.data || {};
      } catch (e) {
        console.warn("Unable to load business profile for PDF", e);
      }

      // Embed the FinLend logo directly into the HTML so the
      // server-side Playwright PDF renderer can always display it.
      let logoDataUrl = "";

      try {
        const logoSource =
          `${window.location.origin}/finlend-logo.png`;

        if (
          typeof logoSource === "string" &&
          logoSource.startsWith("data:")
        ) {
          logoDataUrl = logoSource;
        } else {
          const logoResponse = await fetch(logoSource);
          const logoBlob = await logoResponse.blob();

          logoDataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(logoBlob);
          });
        }
      } catch (e) {
        console.warn("Unable to load FinLend logo for PDF", e);
      }

      const businessName =
        business?.business_name ||
        "FinLend Pvt. Ltd.";

      const ownerName =
        business?.owner_name ||
        "";

      const businessAddress =
        business?.address ||
        "";

      const businessMobile =
        business?.mobile ||
        "";

      const businessEmail =
        business?.email ||
        "";

      const reportRows = rows
        .map(
          (r, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(
                r.customer_name || "Unknown"
              )}</td>
              <td>${escapeHtml(
                r.loan_number ||
                r.loan_no ||
                ""
              )}</td>
              <td>${escapeHtml(
                formatDate(
                  r.paid_date ||
                  searchedStartDate
                )
              )}</td>
              <td class="amount">
                ₹${escapeHtml(money(r.amount))}
              </td>
            </tr>
          `
        )
        .join("");

      const periodText =
        searchedStartDate === searchedEndDate
          ? formatDate(searchedStartDate)
          : `${formatDate(
              searchedStartDate
            )} to ${formatDate(
              searchedEndDate
            )}`;

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />

            <style>
              @page {
                size: A4;
                margin: 14mm 12mm 16mm 12mm;
              }

              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                font-family:
                  Arial,
                  Helvetica,
                  sans-serif;
                color: #172033;
                font-size: 11px;
              }

              .header {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                border-bottom: 3px solid #800020;
                padding: 2px 6px 13px 6px;
                margin-bottom: 18px;
              }

              .logo {
                width: 58px;
                height: 58px;
                object-fit: contain;
                flex: 0 0 58px;
              }

              .business-info {
                flex: 1;
                min-width: 0;
              }

              .brand {
                font-size: 22px;
                font-weight: 800;
                color: #0b2545;
                line-height: 1.15;
                margin-bottom: 4px;
              }

              .business-line {
                color: #64748b;
                font-size: 10px;
                line-height: 1.45;
              }

              .title {
                margin-top: 20px;
                text-align: center;
                font-size: 19px;
                font-weight: 800;
                color: #0b2545;
              }

              .period {
                text-align: center;
                margin-top: 5px;
                color: #475569;
                font-size: 11px;
              }

              .summary {
                margin: 18px 0 14px;
                display: table;
                width: 100%;
                border: 1px solid #cbd5e1;
                border-radius: 6px;
              }

              .summary-item {
                display: table-cell;
                width: 50%;
                padding: 10px 12px;
              }

              .summary-item + .summary-item {
                border-left: 1px solid #cbd5e1;
              }

              .summary-label {
                font-size: 9px;
                text-transform: uppercase;
                letter-spacing: .7px;
                color: #64748b;
                font-weight: 700;
              }

              .summary-value {
                margin-top: 4px;
                color: #0b2545;
                font-size: 15px;
                font-weight: 800;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
              }

              thead {
                display: table-header-group;
              }

              tr {
                page-break-inside: avoid;
              }

              th {
                background: #0b2545;
                color: white;
                padding: 8px 7px;
                border: 1px solid #0b2545;
                font-size: 9px;
                text-transform: uppercase;
                letter-spacing: .4px;
                text-align: left;
              }

              td {
                padding: 7px;
                border: 1px solid #dbe2ea;
                vertical-align: top;
                overflow-wrap: anywhere;
              }

              th:nth-child(1),
              td:nth-child(1) {
                width: 7%;
                text-align: center;
              }

              th:nth-child(2),
              td:nth-child(2) {
                width: 31%;
              }

              th:nth-child(3),
              td:nth-child(3) {
                width: 20%;
              }

              th:nth-child(4),
              td:nth-child(4) {
                width: 20%;
              }

              th:nth-child(5),
              td:nth-child(5) {
                width: 22%;
                text-align: right;
              }

              .amount {
                text-align: right;
                font-weight: 700;
                white-space: nowrap;
              }

              tfoot td {
                background: #f8fafc;
                border-top: 2px solid #0b2545;
                font-weight: 800;
                color: #0b2545;
                padding-top: 10px;
                padding-bottom: 10px;
              }

              .footer-note {
                margin-top: 16px;
                padding-top: 8px;
                border-top: 1px solid #e2e8f0;
                color: #64748b;
                font-size: 9px;
                text-align: center;
              }
            </style>
          </head>

          <body>
            <div class="header">
              ${
                logoDataUrl
                  ? `<img class="logo" src="${escapeHtml(logoDataUrl)}" alt="FinLend Logo" />`
                  : ""
              }

              <div class="business-info">
                <div class="brand">
                  ${escapeHtml(businessName)}
                </div>

                ${
                  ownerName
                    ? `<div class="business-line">${escapeHtml(ownerName)}</div>`
                    : ""
                }

                ${
                  businessAddress
                    ? `<div class="business-line">${escapeHtml(businessAddress)}</div>`
                    : ""
                }

                ${
                  businessMobile
                    ? `<div class="business-line">Mob: ${escapeHtml(businessMobile)}</div>`
                    : ""
                }

                ${
                  businessEmail
                    ? `<div class="business-line">${escapeHtml(businessEmail)}</div>`
                    : ""
                }
              </div>
            </div>

            <div class="title">
              EMI Paid Report
            </div>

            <div class="period">
              Payment Period: ${escapeHtml(periodText)}
            </div>

            <div class="summary">
              <div class="summary-item">
                <div class="summary-label">
                  Total Payments
                </div>

                <div class="summary-value">
                  ${rows.length}
                </div>
              </div>

              <div class="summary-item">
                <div class="summary-label">
                  Total EMI Paid
                </div>

                <div class="summary-value">
                  ₹${escapeHtml(money(total))}
                </div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Customer Name</th>
                  <th>Loan No.</th>
                  <th>Payment Date</th>
                  <th>EMI Paid Amount</th>
                </tr>
              </thead>

              <tbody>
                ${reportRows}
              </tbody>

              <tfoot>
                <tr>
                  <td colspan="4" style="text-align:right;">
                    TOTAL EMI PAID
                  </td>

                  <td class="amount">
                    ₹${escapeHtml(money(total))}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div class="footer-note">
              Generated securely by FinLend
            </div>
          </body>
        </html>
      `;

      const response = await fetch(
        "/api/print/pdf",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },

          body: JSON.stringify({
            html,
            title:
              `FinLend EMI Paid ${searchedStartDate} to ${searchedEndDate}`
          })
        }
      );

      if (!response.ok) {
        let message =
          "Unable to generate the FinLend PDF.";

        try {
          const data = await response.json();

          if (data?.detail) {
            message = String(data.detail);
          }
        } catch (_) {}

        throw new Error(message);
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error(
          "The PDF response was empty."
        );
      }

      const pdfUrl =
        URL.createObjectURL(blob);

      popup.location.href = pdfUrl;

      setTimeout(() => {
        try {
          URL.revokeObjectURL(pdfUrl);
        } catch (_) {}
      }, 10 * 60 * 1000);

    } catch (err) {
      console.error(
        "FinLend EMI Paid PDF error:",
        err
      );

      try {
        popup.document.open();

        popup.document.write(`
          <!DOCTYPE html>
          <html>
            <body style="
              font-family:Arial,sans-serif;
              padding:30px;
              color:#991b1b;
            ">
              <h3>Unable to generate PDF</h3>
              <p>
                ${escapeHtml(
                  err?.message ||
                  "Please try again."
                )}
              </p>
            </body>
          </html>
        `);

        popup.document.close();

      } catch (_) {}

    } finally {
      setPrinting(false);
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        background: "#f7f7f7",
        minHeight: "100vh"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
          flexWrap: "wrap"
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>
            EMI Paid
          </h2>

          <div
            style={{
              marginTop: "5px",
              color: "#666"
            }}
          >
            EMI payments received during selected period
          </div>
        </div>

        <button
          onClick={() => navigate("/")}
          style={{
            padding: "10px 18px",
            border: 0,
            borderRadius: "8px",
            background: "#555",
            color: "#fff",
            cursor: "pointer"
          }}
        >
          Back
        </button>
      </div>

      <div
        style={{
          background: "#fff",
          padding: "18px",
          borderRadius: "10px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "end",
          gap: "15px",
          flexWrap: "wrap"
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "7px",
              fontWeight: "600"
            }}
          >
            Start Date
          </label>

          <input
            type="date"
            value={startDate}
            onChange={(e) =>
              setStartDate(e.target.value)
            }
            style={{
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "7px",
              fontSize: "16px"
            }}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "7px",
              fontWeight: "600"
            }}
          >
            End Date
          </label>

          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) =>
              setEndDate(e.target.value)
            }
            style={{
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "7px",
              fontSize: "16px"
            }}
          />
        </div>

        <button
          onClick={() =>
            load(startDate, endDate)
          }
          disabled={loading}
          style={{
            padding: "10px 20px",
            border: 0,
            borderRadius: "7px",
            background: "#b00020",
            color: "#fff",
            fontWeight: "600",
            cursor: loading
              ? "not-allowed"
              : "pointer",
            opacity: loading ? 0.65 : 1
          }}
        >
          {loading ? "Searching..." : "Search"}
        </button>

        <button
          onClick={printSecurePdf}
          disabled={
            printing ||
            loading ||
            rows.length === 0
          }
          style={{
            padding: "10px 20px",
            border: 0,
            borderRadius: "7px",
            background: "#0b2545",
            color: "#fff",
            fontWeight: "600",
            cursor:
              printing ||
              loading ||
              rows.length === 0
                ? "not-allowed"
                : "pointer",
            opacity:
              printing ||
              loading ||
              rows.length === 0
                ? 0.6
                : 1
          }}
        >
          {printing
            ? "Generating PDF..."
            : "Print / Save PDF"}
        </button>
      </div>

      <div
        style={{
          background: "#fff",
          padding: "18px",
          borderRadius: "10px"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "15px",
            fontWeight: "600",
            gap: "15px",
            flexWrap: "wrap"
          }}
        >
          <span>
            EMI Paid:{" "}
            {formatDate(searchedStartDate)}
            {searchedStartDate !==
              searchedEndDate &&
              ` to ${formatDate(
                searchedEndDate
              )}`}
          </span>

          <span>
            Total: ₹{money(total)}
          </span>
        </div>

        {loading && (
          <div>Loading...</div>
        )}

        {error && (
          <div
            style={{
              color: "#b00020",
              padding: "10px 0"
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div
            style={{
              overflowX: "auto"
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse"
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f0f0f0"
                  }}
                >
                  <th style={th}>
                    Sl. No.
                  </th>

                  <th style={th}>
                    Customer Name
                  </th>

                  <th style={th}>
                    Loan No.
                  </th>

                  <th style={th}>
                    Payment Date
                  </th>

                  <th
                    style={{
                      ...th,
                      textAlign: "right"
                    }}
                  >
                    EMI Paid Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        padding: "25px",
                        textAlign: "center"
                      }}
                    >
                      No EMI payments found for this period.
                    </td>
                  </tr>
                ) : (
                  rows.map(
                    (r, index) => (
                      <tr
                        key={
                          r.id ||
                          `${r.loan_no}-${r.paid_date}-${index}`
                        }
                      >
                        <td style={td}>
                          {index + 1}
                        </td>

                        <td style={td}>
                          {r.customer_name ||
                            "Unknown"}
                        </td>

                        <td style={td}>
                          {r.loan_number ||
                            r.loan_no ||
                            ""}
                        </td>

                        <td style={td}>
                          {formatDate(
                            r.paid_date
                          )}
                        </td>

                        <td
                          style={{
                            ...td,
                            textAlign: "right",
                            fontWeight: "600"
                          }}
                        >
                          ₹{money(r.amount)}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td
                      colSpan="4"
                      style={{
                        ...td,
                        fontWeight: "700",
                        textAlign: "right"
                      }}
                    >
                      Total
                    </td>

                    <td
                      style={{
                        ...td,
                        fontWeight: "700",
                        textAlign: "right"
                      }}
                    >
                      ₹{money(total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const th = {
  padding: "12px 10px",
  borderBottom: "2px solid #ddd",
  textAlign: "left",
  whiteSpace: "nowrap"
};

const td = {
  padding: "11px 10px",
  borderBottom: "1px solid #eee"
};
